/**
 * The journal dialog: the shelf of read documents, on request, in a modal.
 *
 * @remarks
 * The map dialog's pattern, document-side, for the map dialog's reasons: a
 * native `<dialog>` + `showModal()` buys focus trapping, `Escape`, the top
 * layer, an inert background and focus restoration — everything a hand-rolled
 * `role="dialog"` re-implements incompletely. jsdom lacks `showModal`, so the
 * shared jsdom setup shims it; the real behavior is Playwright + axe
 * territory.
 *
 * The component owns only "is it open" — the shelf itself stays
 * {@link JournalPanelComponent}, pure and prop-driven, so exactly one thing
 * knows how to render a document.
 *
 * @packageDocumentation
 */

import { type JournalEntryView } from '@orpheus/api-contracts';
import { useState } from 'react';

import { JournalPanelComponent } from '../journal-panel/journal-panel-component.js';
import { ModalCornerCloseComponent } from '../modal-dialog/modal-corner-close-component.js';
import { showModalWhileMounted } from '../modal-dialog/show-modal-while-mounted.js';

/** Props for {@link JournalDialogComponent}. */
export interface JournalDialogComponentProps {
	/** The engine's journal derivation — passed through to the panel. */
	readonly entries: readonly JournalEntryView[];
	/**
	 * Called once the dialog has closed, by any route — the Close button, the corner ✕ or
	 * `Escape`. The native dialog returns focus to the trigger; the shell uses
	 * this to send it on to the directive line instead, so a player who
	 * consulted the journal can type the next move without a click (Mike,
	 * 2026-09-10). Optional: without it, the native behavior stands.
	 */
	readonly onDismissed?: () => void;
}

/**
 * Render the journal trigger and its modal dialog.
 *
 * @param props - {@link JournalDialogComponentProps}
 * @returns The trigger button and the dialog it opens
 */
export const JournalDialogComponent = ({ entries, onDismissed }: JournalDialogComponentProps) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			<button
				aria-haspopup="dialog"
				// Named in full for assistive tech; the visible chrome stays compact.
				aria-label={`Journal — ${String(entries.length)} document${entries.length === 1 ? '' : 's'} read`}
				className="journal-dialog__trigger"
				onClick={() => {
					setIsOpen(true);
				}}
				type="button"
			>
				<JournalIcon />
				<span className="journal-dialog__trigger-label">Journal</span>
				<span aria-hidden="true" className="journal-dialog__trigger-count">
					{entries.length}
				</span>
			</button>
			{isOpen ? (
				<dialog
					aria-labelledby="journal-panel-heading"
					className="journal-dialog"
					// The ONE route out. Escape closes the element natively; the Close
					// button closes it too (below) rather than dropping the flag first —
					// while the modal is up the page behind it is inert, so focus sent
					// to the directive line then would be refused. Only once the
					// element has closed (and the browser has put focus back on the
					// trigger) can the shell send it on, which is what `onDismissed`
					// is for.
					onClose={() => {
						setIsOpen(false);
						onDismissed?.();
					}}
					// The element exists exactly while the journal is open — the map
					// dialog's construction, through the one shared ref (D172).
					ref={showModalWhileMounted}
				>
					<div className="journal-dialog__body">
						<JournalPanelComponent entries={entries} />
						<button
							className="journal-dialog__close"
							onClick={(event) => {
								event.currentTarget.closest('dialog')?.close();
							}}
							type="button"
						>
							Close
						</button>
					</div>
					<ModalCornerCloseComponent label="Close journal" />
				</dialog>
			) : null}
		</>
	);
};

/**
 * The trigger's bound-pages glyph. Decorative — the button is named by its
 * visible label, so the icon is hidden from assistive technology.
 *
 * @returns The icon
 */
const JournalIcon = () => (
	<svg
		aria-hidden="true"
		className="journal-dialog__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v18H6.5A2.5 2.5 0 0 0 4 22.5V4.5Z" />
		<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
		<path d="M9 7h7" />
	</svg>
);
